/**
 * V2 homepage translations — Amanah by Zakat.International
 *
 * Flat dot-path key/value dictionary, one block per UI language.
 * Loaded by `getTranslation()` in `translations.js` as a third lookup
 * tier so English acts as the automatic fallback when a language
 * doesn't have a specific `v2.*` key yet.
 *
 * Covers the surfaces added during the V2 modernisation:
 *   • HeroV2            — hero headline, tagline, CTAs, stats labels
 *   • ProductCardsV2    — 3 product tiles (Zakat / Faraid / Wasiyyah)
 *   • Community-tools   — "share the good" section
 *   • FAQSection        — Knowledge Centre chrome + 10 Q&A + chips
 *
 * NB: religious/technical shorthand ("faraid", "wasiyyah", "nisab",
 * "hadith", "hijri", "awl", "radd", "hajb", "mahr", "hiba") is left
 * transliterated in most languages since these are recognised terms
 * for the target audience.
 */

const en = {
  // --- HeroV2 ---
  'v2.hero.kicker': 'Trusted by Muslims in 95 countries',
  'v2.hero.title.a': 'Helping Muslims Protect',
  'v2.hero.title.b': 'Wealth, Family',
  'v2.hero.title.c': 'and',
  'v2.hero.title.d': 'Legacy',
  'v2.hero.subtitle': 'One private toolkit for Zakat, Faraid and Wasiyyah.',
  'v2.hero.subtitle.tail': 'No accounts. No tracking. Free forever.',
  'v2.hero.cta.primary': 'Choose Your Journey',
  'v2.hero.cta.kit': 'Life-planning kit',
  'v2.hero.cta.kitBusy': 'Preparing…',
  'v2.hero.cta.country': '95 country guides',
  'v2.hero.stats.muslims': 'Muslims',
  'v2.hero.stats.countries': 'Countries',
  'v2.hero.stats.languages': 'Languages',
  'v2.hero.stats.private': 'Private',
  'v2.hero.stats.stored': 'Stored',

  // --- ProductCardsV2 ---
  'v2.cards.kicker': 'Choose your journey',
  'v2.cards.zakat.title': 'Personal Zakat Calculator',
  'v2.cards.zakat.subtitle': 'On gold, savings, property, business & investments.',
  'v2.cards.zakat.time': '~2 min',
  'v2.cards.zakat.cta': 'Start calculating',
  'v2.cards.zakat.strap': 'Purification',
  'v2.cards.zakat.benefits': '5 madhhabs · Live nisab · Currency-aware',
  'v2.cards.faraid.title': 'Inheritance Calculator',
  'v2.cards.faraid.subtitle': 'Faraid shares — with confidence and clarity.',
  'v2.cards.faraid.time': '~4 min',
  'v2.cards.faraid.cta': 'Compute shares',
  'v2.cards.faraid.strap': 'Distribution',
  'v2.cards.faraid.benefits': 'All schools · Family tree · Awl · Radd · Hajb',
  'v2.cards.will.title': 'Islamic Will Writer',
  'v2.cards.will.subtitle': 'Shariah-aligned Wasiyyah — protect your loved ones.',
  'v2.cards.will.time': '~8 min',
  'v2.cards.will.cta': 'Draft your will',
  'v2.cards.will.strap': 'Legacy',
  'v2.cards.will.benefits': '1/3 cap · Guardianship · Family letter',

  // --- Community tools ---
  'v2.community.kicker': 'Share the good',
  'v2.community.title.a': 'Take Amanah by Zakat.International',
  'v2.community.title.b': 'to your community',
  'v2.community.subtitle': 'A free estate-planning kit — Zakat, Inheritance and Will worksheets in Word + CSV — and a printable A4 masjid poster with a QR code for your local mosque board.',

  // --- FAQ chrome ---
  'v2.faq.kicker': 'Knowledge Centre',
  'v2.faq.title.a': 'Zakat, Faraid & Wasiyyah —',
  'v2.faq.title.b': 'answered',
  'v2.faq.subtitle': 'Concise answers referenced against the Qur\u2019anic verse or hadith you can look up yourself.',
  'v2.faq.search': 'Ask anything \u2014 silver nisab, 1/3 wasiyyah, data storage\u2026',
  'v2.faq.cat.all': 'All',
  'v2.faq.cat.zakat': 'Zakat',
  'v2.faq.cat.faraid': 'Faraid',
  'v2.faq.cat.wasiyyah': 'Wasiyyah',
  'v2.faq.cat.privacy': 'Privacy',
  'v2.faq.count.answer': 'answer',
  'v2.faq.count.answers': 'answers',
  'v2.faq.empty.title': 'We haven\u2019t covered that one yet.',
  'v2.faq.empty.body': 'Try a shorter phrase or a different category \u2014 or ask the AI Advisor on the inheritance result page.',
  'v2.faq.footer': 'Can\u2019t find what you\u2019re looking for? Open the AI Advisor on the inheritance result page \u2014 it explains your specific case in plain language, without ever leaving your browser.',
};

const ar = {
  'v2.hero.kicker': 'موثوق من قِبل المسلمين في 95 دولة',
  'v2.hero.title.a': 'نساعد المسلمين على حماية',
  'v2.hero.title.b': 'المال والعائلة',
  'v2.hero.title.c': 'و',
  'v2.hero.title.d': 'الإرث',
  'v2.hero.subtitle': 'أدوات خاصة موحدة للزكاة والفرائض والوصية.',
  'v2.hero.subtitle.tail': 'بلا حسابات. بلا تتبع. مجانية دائماً.',
  'v2.hero.cta.primary': 'ابدأ رحلتك',
  'v2.hero.cta.kit': 'حقيبة التخطيط الحياتي',
  'v2.hero.cta.kitBusy': 'جارٍ التحضير…',
  'v2.hero.cta.country': 'أدلة 95 دولة',
  'v2.hero.stats.muslims': 'مسلم',
  'v2.hero.stats.countries': 'دولة',
  'v2.hero.stats.languages': 'لغة',
  'v2.hero.stats.private': 'خاص',
  'v2.hero.stats.stored': 'محفوظ',
  'v2.cards.kicker': 'اختر رحلتك',
  'v2.cards.zakat.title': 'احسب الزكاة',
  'v2.cards.zakat.subtitle': 'على الذهب والمدخرات والعقارات والأعمال والاستثمارات.',
  'v2.cards.zakat.time': '~٢ دقيقة',
  'v2.cards.zakat.cta': 'ابدأ الحساب',
  'v2.cards.zakat.strap': 'تطهير',
  'v2.cards.zakat.benefits': '٥ مذاهب · نصاب حي · متعدد العملات',
  'v2.cards.faraid.title': 'الميراث الإسلامي',
  'v2.cards.faraid.subtitle': 'أنصبة الفرائض — بثقة ووضوح.',
  'v2.cards.faraid.time': '~٤ دقائق',
  'v2.cards.faraid.cta': 'احسب الأنصبة',
  'v2.cards.faraid.strap': 'توزيع',
  'v2.cards.faraid.benefits': 'جميع المذاهب · شجرة العائلة · العول · الرد · الحجب',
  'v2.cards.will.title': 'الوصية الإسلامية',
  'v2.cards.will.subtitle': 'وصية موافقة للشريعة — احمِ من تحب.',
  'v2.cards.will.time': '~٨ دقائق',
  'v2.cards.will.cta': 'اكتب وصيتك',
  'v2.cards.will.strap': 'الإرث',
  'v2.cards.will.benefits': 'حد الثلث · الوصاية · رسالة العائلة',
  'v2.community.kicker': 'انشر الخير',
  'v2.community.title.a': 'خُذ زكاة إنترناشيونال',
  'v2.community.title.b': 'إلى مجتمعك',
  'v2.community.subtitle': 'حقيبة تخطيط تركة مجانية بـ 8 قوالب لك، وملصق مسجد قابل للطباعة بحجم A4 مع رمز QR للوحة مسجدك.',
  'v2.faq.kicker': 'مركز المعرفة',
  'v2.faq.title.a': 'الزكاة والفرائض والوصية —',
  'v2.faq.title.b': 'إجابات',
  'v2.faq.subtitle': 'إجابات موجزة مقرونة بالآيات القرآنية والأحاديث حتى تراجعها بنفسك.',
  'v2.faq.search': 'اسأل أي شيء — نصاب الفضة، حد الوصية، تخزين البيانات…',
  'v2.faq.cat.all': 'الكل',
  'v2.faq.cat.zakat': 'الزكاة',
  'v2.faq.cat.faraid': 'الفرائض',
  'v2.faq.cat.wasiyyah': 'الوصية',
  'v2.faq.cat.privacy': 'الخصوصية',
  'v2.faq.count.answer': 'إجابة',
  'v2.faq.count.answers': 'إجابات',
  'v2.faq.empty.title': 'لم نتطرق إلى هذا بعد.',
  'v2.faq.empty.body': 'جرّب عبارة أقصر أو فئة مختلفة — أو اسأل المستشار الذكي في صفحة نتيجة الميراث.',
  'v2.faq.footer': 'لم تجد ما تبحث عنه؟ افتح المستشار الذكي في صفحة نتيجة الميراث — سيشرح حالتك بلغة واضحة، دون أن يغادر متصفحك.',
};

const ur = {
  'v2.hero.kicker': '95 ممالک کے مسلمانوں کا اعتماد',
  'v2.hero.title.a': 'مسلمانوں کی مدد',
  'v2.hero.title.b': 'مال، خاندان',
  'v2.hero.title.c': 'اور',
  'v2.hero.title.d': 'وراثت کے تحفظ میں',
  'v2.hero.subtitle': 'زکات، فرائض اور وصیت کے لیے ایک نجی ٹول کٹ۔',
  'v2.hero.subtitle.tail': 'اکاؤنٹ کی ضرورت نہیں۔ کوئی ٹریکنگ نہیں۔ ہمیشہ مفت۔',
  'v2.hero.cta.primary': 'اپنا سفر شروع کریں',
  'v2.hero.cta.kit': 'زندگی کی منصوبہ بندی کٹ',
  'v2.hero.cta.kitBusy': 'تیار ہو رہا ہے…',
  'v2.hero.cta.country': '95 ممالک کی گائیڈز',
  'v2.hero.stats.muslims': 'مسلمان',
  'v2.hero.stats.countries': 'ممالک',
  'v2.hero.stats.languages': 'زبانیں',
  'v2.hero.stats.private': 'نجی',
  'v2.hero.stats.stored': 'محفوظ',
  'v2.cards.kicker': 'اپنا سفر منتخب کریں',
  'v2.cards.zakat.title': 'زکات کا حساب لگائیں',
  'v2.cards.zakat.subtitle': 'سونا، بچت، جائیداد، کاروبار اور سرمایہ کاری پر۔',
  'v2.cards.zakat.time': '~2 منٹ',
  'v2.cards.zakat.cta': 'حساب شروع کریں',
  'v2.cards.zakat.strap': 'تطہیر',
  'v2.cards.zakat.benefits': '5 مذاہب · لائیو نصاب · ہر کرنسی میں',
  'v2.cards.faraid.title': 'اسلامی وراثت',
  'v2.cards.faraid.subtitle': 'فرائض کے حصے — اعتماد اور وضاحت کے ساتھ۔',
  'v2.cards.faraid.time': '~4 منٹ',
  'v2.cards.faraid.cta': 'حصے شمار کریں',
  'v2.cards.faraid.strap': 'تقسیم',
  'v2.cards.faraid.benefits': 'تمام مذاہب · خاندانی شجرہ · عول · رد · حجب',
  'v2.cards.will.title': 'اسلامی وصیت',
  'v2.cards.will.subtitle': 'شریعت کے مطابق وصیت — اپنے پیاروں کی حفاظت کریں۔',
  'v2.cards.will.time': '~8 منٹ',
  'v2.cards.will.cta': 'اپنی وصیت لکھیں',
  'v2.cards.will.strap': 'وراثت',
  'v2.cards.will.benefits': '1/3 کی حد · سرپرستی · خاندانی خط',
  'v2.community.kicker': 'خیر پھیلائیں',
  'v2.community.title.a': 'زکات انٹرنیشنل کو اپنی',
  'v2.community.title.b': 'کمیونٹی تک لے جائیں',
  'v2.community.subtitle': 'آپ کے لیے 8 ٹیمپلیٹس پر مبنی مفت اسٹیٹ پلاننگ کٹ، اور آپ کی مقامی مسجد کے بورڈ کے لیے QR کوڈ کے ساتھ قابلِ پرنٹ A4 مسجد پوسٹر۔',
  'v2.faq.kicker': 'علمی مرکز',
  'v2.faq.title.a': 'زکات، فرائض اور وصیت —',
  'v2.faq.title.b': 'جوابات',
  'v2.faq.subtitle': 'مختصر جوابات جو آپ خود دیکھ سکنے والی قرآنی آیت یا حدیث کے حوالے سے دیے گئے ہیں۔',
  'v2.faq.search': 'کچھ بھی پوچھیں — چاندی کا نصاب، 1/3 وصیت، ڈیٹا محفوظ کرنا…',
  'v2.faq.cat.all': 'سب',
  'v2.faq.cat.zakat': 'زکات',
  'v2.faq.cat.faraid': 'فرائض',
  'v2.faq.cat.wasiyyah': 'وصیت',
  'v2.faq.cat.privacy': 'رازداری',
  'v2.faq.count.answer': 'جواب',
  'v2.faq.count.answers': 'جوابات',
  'v2.faq.empty.title': 'ہم نے ابھی یہ نہیں چھوا۔',
  'v2.faq.empty.body': 'مختصر جملہ یا مختلف زمرہ آزمائیں — یا وراثت کے نتیجہ کے صفحہ پر AI مشیر سے پوچھیں۔',
  'v2.faq.footer': 'جو ڈھونڈ رہے ہیں نہیں ملا؟ وراثت کے نتیجہ کے صفحہ پر AI مشیر کھولیں — وہ آپ کے مخصوص کیس کو سادہ زبان میں سمجھائے گا، آپ کے براؤزر سے کبھی باہر نکلے بغیر۔',
};

const fr = {
  'v2.hero.kicker': 'Approuvé par les musulmans dans 95 pays',
  'v2.hero.title.a': 'Aider les musulmans à protéger',
  'v2.hero.title.b': 'la richesse, la famille',
  'v2.hero.title.c': 'et',
  'v2.hero.title.d': 'l\u2019héritage',
  'v2.hero.subtitle': 'Une boîte à outils privée pour la Zakat, le Faraid et la Wasiyyah.',
  'v2.hero.subtitle.tail': 'Aucun compte. Aucun suivi. Toujours gratuit.',
  'v2.hero.cta.primary': 'Choisissez votre parcours',
  'v2.hero.cta.kit': 'Kit de planification',
  'v2.hero.cta.kitBusy': 'Préparation…',
  'v2.hero.cta.country': '95 guides pays',
  'v2.hero.stats.muslims': 'Musulmans',
  'v2.hero.stats.countries': 'Pays',
  'v2.hero.stats.languages': 'Langues',
  'v2.hero.stats.private': 'Privé',
  'v2.hero.stats.stored': 'Stocké',
  'v2.cards.kicker': 'Choisissez votre parcours',
  'v2.cards.zakat.title': 'Calculer la Zakat',
  'v2.cards.zakat.subtitle': 'Sur l\u2019or, l\u2019épargne, l\u2019immobilier, les entreprises et les placements.',
  'v2.cards.zakat.time': '~2 min',
  'v2.cards.zakat.cta': 'Commencer',
  'v2.cards.zakat.strap': 'Purification',
  'v2.cards.zakat.benefits': '5 madhabs · Nisab en direct · Multi-devises',
  'v2.cards.faraid.title': 'Héritage islamique',
  'v2.cards.faraid.subtitle': 'Parts du Faraid — avec confiance et clarté.',
  'v2.cards.faraid.time': '~4 min',
  'v2.cards.faraid.cta': 'Calculer les parts',
  'v2.cards.faraid.strap': 'Distribution',
  'v2.cards.faraid.benefits': 'Toutes écoles · Arbre familial · Awl · Radd · Hajb',
  'v2.cards.will.title': 'Testament islamique (Wasiyyah)',
  'v2.cards.will.subtitle': 'Testament conforme à la charia — protégez vos proches.',
  'v2.cards.will.time': '~8 min',
  'v2.cards.will.cta': 'Rédiger votre testament',
  'v2.cards.will.strap': 'Héritage',
  'v2.cards.will.benefits': 'Limite 1/3 · Tutelle · Lettre familiale',
  'v2.community.kicker': 'Partagez le bien',
  'v2.community.title.a': 'Emportez Amanah by Zakat.International',
  'v2.community.title.b': 'dans votre communauté',
  'v2.community.subtitle': 'Un kit de planification successorale gratuit à 8 modèles pour vous, et une affiche mosquée A4 imprimable avec QR code pour le tableau de votre mosquée locale.',
  'v2.faq.kicker': 'Centre de savoir',
  'v2.faq.title.a': 'Zakat, Faraid et Wasiyyah —',
  'v2.faq.title.b': 'répondus',
  'v2.faq.subtitle': 'Réponses concises référencées à un verset coranique ou un hadith que vous pouvez vérifier vous-même.',
  'v2.faq.search': 'Demandez tout — nisab argent, wasiyyah 1/3, stockage des données…',
  'v2.faq.cat.all': 'Tout',
  'v2.faq.cat.zakat': 'Zakat',
  'v2.faq.cat.faraid': 'Faraid',
  'v2.faq.cat.wasiyyah': 'Wasiyyah',
  'v2.faq.cat.privacy': 'Confidentialité',
  'v2.faq.count.answer': 'réponse',
  'v2.faq.count.answers': 'réponses',
  'v2.faq.empty.title': 'Nous n\u2019avons pas encore traité cela.',
  'v2.faq.empty.body': 'Essayez une phrase plus courte ou une autre catégorie — ou demandez au conseiller IA sur la page de résultat d\u2019héritage.',
  'v2.faq.footer': 'Vous ne trouvez pas ? Ouvrez le conseiller IA sur la page de résultat d\u2019héritage — il explique votre cas en langage simple, sans jamais quitter votre navigateur.',
};

const ms = {
  'v2.hero.kicker': 'Dipercayai oleh Muslim di 95 negara',
  'v2.hero.title.a': 'Membantu Muslim melindungi',
  'v2.hero.title.b': 'harta, keluarga',
  'v2.hero.title.c': 'dan',
  'v2.hero.title.d': 'warisan',
  'v2.hero.subtitle': 'Satu peralatan peribadi untuk Zakat, Faraid dan Wasiyyah.',
  'v2.hero.subtitle.tail': 'Tiada akaun. Tiada penjejakan. Percuma selamanya.',
  'v2.hero.cta.primary': 'Pilih Perjalanan Anda',
  'v2.hero.cta.kit': 'Kit perancangan hidup',
  'v2.hero.cta.kitBusy': 'Menyediakan…',
  'v2.hero.cta.country': 'Panduan 95 negara',
  'v2.hero.stats.muslims': 'Muslim',
  'v2.hero.stats.countries': 'Negara',
  'v2.hero.stats.languages': 'Bahasa',
  'v2.hero.stats.private': 'Peribadi',
  'v2.hero.stats.stored': 'Disimpan',
  'v2.cards.kicker': 'Pilih perjalanan anda',
  'v2.cards.zakat.title': 'Kira Zakat',
  'v2.cards.zakat.subtitle': 'Emas, simpanan, hartanah, perniagaan & pelaburan.',
  'v2.cards.zakat.time': '~2 min',
  'v2.cards.zakat.cta': 'Mula mengira',
  'v2.cards.zakat.strap': 'Penyucian',
  'v2.cards.zakat.benefits': '5 mazhab · Nisab langsung · Pelbagai mata wang',
  'v2.cards.faraid.title': 'Faraid (Pusaka)',
  'v2.cards.faraid.subtitle': 'Bahagian Faraid — dengan yakin dan jelas.',
  'v2.cards.faraid.time': '~4 min',
  'v2.cards.faraid.cta': 'Kira bahagian',
  'v2.cards.faraid.strap': 'Pembahagian',
  'v2.cards.faraid.benefits': 'Semua mazhab · Salasilah · Awl · Radd · Hajb',
  'v2.cards.will.title': 'Wasiat Islam (Wasiyyah)',
  'v2.cards.will.subtitle': 'Wasiat menepati syariah — lindungi orang tersayang.',
  'v2.cards.will.time': '~8 min',
  'v2.cards.will.cta': 'Draf wasiat anda',
  'v2.cards.will.strap': 'Legasi',
  'v2.cards.will.benefits': 'Had 1/3 · Penjagaan · Surat keluarga',
  'v2.community.kicker': 'Kongsi kebaikan',
  'v2.community.title.a': 'Bawa Amanah by Zakat.International',
  'v2.community.title.b': 'ke komuniti anda',
  'v2.community.subtitle': 'Kit perancangan harta pusaka percuma dengan 8 templat untuk anda, dan poster masjid A4 boleh cetak dengan kod QR untuk papan masjid tempatan.',
  'v2.faq.kicker': 'Pusat Pengetahuan',
  'v2.faq.title.a': 'Zakat, Faraid & Wasiyyah —',
  'v2.faq.title.b': 'terjawab',
  'v2.faq.subtitle': 'Jawapan ringkas dirujuk pada ayat al-Qur\u2019an atau hadis yang boleh anda semak sendiri.',
  'v2.faq.search': 'Tanya apa sahaja — nisab perak, wasiyyah 1/3, simpanan data…',
  'v2.faq.cat.all': 'Semua',
  'v2.faq.cat.zakat': 'Zakat',
  'v2.faq.cat.faraid': 'Faraid',
  'v2.faq.cat.wasiyyah': 'Wasiyyah',
  'v2.faq.cat.privacy': 'Privasi',
  'v2.faq.count.answer': 'jawapan',
  'v2.faq.count.answers': 'jawapan',
  'v2.faq.empty.title': 'Kami belum tangani soalan itu lagi.',
  'v2.faq.empty.body': 'Cuba frasa lebih pendek atau kategori berbeza — atau tanya Penasihat AI pada halaman keputusan Faraid.',
  'v2.faq.footer': 'Tidak menemui yang dicari? Buka Penasihat AI pada halaman keputusan Faraid — ia menerangkan kes anda dalam bahasa mudah, tanpa meninggalkan pelayar anda.',
};

const tr = {
  'v2.hero.kicker': '95 ülkedeki Müslümanların güvendiği',
  'v2.hero.title.a': 'Müslümanların korumasına yardımcı oluyoruz:',
  'v2.hero.title.b': 'servet, aile',
  'v2.hero.title.c': 've',
  'v2.hero.title.d': 'miras',
  'v2.hero.subtitle': 'Zekât, feraiz ve vasiyet için tek ve özel bir araç seti.',
  'v2.hero.subtitle.tail': 'Hesap yok. Takip yok. Sonsuza dek ücretsiz.',
  'v2.hero.cta.primary': 'Yolculuğunuzu seçin',
  'v2.hero.cta.kit': 'Hayat planlama kiti',
  'v2.hero.cta.kitBusy': 'Hazırlanıyor…',
  'v2.hero.cta.country': '95 ülke rehberi',
  'v2.hero.stats.muslims': 'Müslüman',
  'v2.hero.stats.countries': 'Ülke',
  'v2.hero.stats.languages': 'Dil',
  'v2.hero.stats.private': 'Özel',
  'v2.hero.stats.stored': 'Saklanan',
  'v2.cards.kicker': 'Yolculuğunuzu seçin',
  'v2.cards.zakat.title': 'Zekât hesapla',
  'v2.cards.zakat.subtitle': 'Altın, birikim, gayrimenkul, iş ve yatırımlar.',
  'v2.cards.zakat.time': '~2 dk',
  'v2.cards.zakat.cta': 'Hesaplamaya başla',
  'v2.cards.zakat.strap': 'Arınma',
  'v2.cards.zakat.benefits': '5 mezhep · Canlı nisap · Çoklu para birimi',
  'v2.cards.faraid.title': 'İslami miras',
  'v2.cards.faraid.subtitle': 'Feraiz payları — güvenle ve netlikle.',
  'v2.cards.faraid.time': '~4 dk',
  'v2.cards.faraid.cta': 'Payları hesapla',
  'v2.cards.faraid.strap': 'Dağıtım',
  'v2.cards.faraid.benefits': 'Tüm mezhepler · Aile ağacı · Avl · Redd · Hacb',
  'v2.cards.will.title': 'İslami vasiyet (Vasiyye)',
  'v2.cards.will.subtitle': 'Şeriata uygun vasiyet — sevdiklerinizi koruyun.',
  'v2.cards.will.time': '~8 dk',
  'v2.cards.will.cta': 'Vasiyetinizi taslak yapın',
  'v2.cards.will.strap': 'Miras',
  'v2.cards.will.benefits': '1/3 sınırı · Vesayet · Aile mektubu',
  'v2.community.kicker': 'Hayrı paylaşın',
  'v2.community.title.a': 'Amanah by Zakat.International\u2019ı',
  'v2.community.title.b': 'topluluğunuza götürün',
  'v2.community.subtitle': 'Sizin için 8 şablonlu ücretsiz miras planlama kiti ve yerel cami panonuz için QR kodlu, yazdırılabilir A4 cami posteri.',
  'v2.faq.kicker': 'Bilgi Merkezi',
  'v2.faq.title.a': 'Zekât, feraiz ve vasiyet —',
  'v2.faq.title.b': 'cevaplandı',
  'v2.faq.subtitle': 'Kendi araştırabileceğiniz Kur\u2019an ayeti veya hadise dayanan kısa cevaplar.',
  'v2.faq.search': 'Sorun — gümüş nisap, 1/3 vasiyet, veri saklama…',
  'v2.faq.cat.all': 'Hepsi',
  'v2.faq.cat.zakat': 'Zekât',
  'v2.faq.cat.faraid': 'Feraiz',
  'v2.faq.cat.wasiyyah': 'Vasiyet',
  'v2.faq.cat.privacy': 'Gizlilik',
  'v2.faq.count.answer': 'cevap',
  'v2.faq.count.answers': 'cevap',
  'v2.faq.empty.title': 'Bunu henüz ele almadık.',
  'v2.faq.empty.body': 'Daha kısa bir ifade veya farklı bir kategori deneyin — ya da miras sonucu sayfasındaki AI Danışmanına sorun.',
  'v2.faq.footer': 'Aradığınızı bulamadınız mı? Miras sonucu sayfasındaki AI Danışmanı\u2019nı açın — durumunuzu sade dille açıklar, tarayıcınızdan hiç ayrılmadan.',
};

const ru = {
  'v2.hero.kicker': 'Мусульмане из 95 стран доверяют нам',
  'v2.hero.title.a': 'Помогаем мусульманам защитить',
  'v2.hero.title.b': 'богатство, семью',
  'v2.hero.title.c': 'и',
  'v2.hero.title.d': 'наследие',
  'v2.hero.subtitle': 'Приватный набор инструментов для закята, фараида и васыи.',
  'v2.hero.subtitle.tail': 'Без аккаунтов. Без отслеживания. Всегда бесплатно.',
  'v2.hero.cta.primary': 'Выберите свой путь',
  'v2.hero.cta.kit': 'Набор планирования жизни',
  'v2.hero.cta.kitBusy': 'Подготовка…',
  'v2.hero.cta.country': 'Гиды по 95 странам',
  'v2.hero.stats.muslims': 'Мусульман',
  'v2.hero.stats.countries': 'Стран',
  'v2.hero.stats.languages': 'Языков',
  'v2.hero.stats.private': 'Приватно',
  'v2.hero.stats.stored': 'Сохранено',
  'v2.cards.kicker': 'Выберите свой путь',
  'v2.cards.zakat.title': 'Рассчитать закят',
  'v2.cards.zakat.subtitle': 'Золото, сбережения, недвижимость, бизнес и инвестиции.',
  'v2.cards.zakat.time': '~2 мин',
  'v2.cards.zakat.cta': 'Начать расчёт',
  'v2.cards.zakat.strap': 'Очищение',
  'v2.cards.zakat.benefits': '5 мазхабов · Живой нисаб · Многовалютность',
  'v2.cards.faraid.title': 'Исламское наследство',
  'v2.cards.faraid.subtitle': 'Доли фараида — с уверенностью и ясностью.',
  'v2.cards.faraid.time': '~4 мин',
  'v2.cards.faraid.cta': 'Рассчитать доли',
  'v2.cards.faraid.strap': 'Распределение',
  'v2.cards.faraid.benefits': 'Все мазхабы · Древо семьи · Авль · Радд · Хаджб',
  'v2.cards.will.title': 'Исламское завещание (васыя)',
  'v2.cards.will.subtitle': 'Завещание по шариату — защитите близких.',
  'v2.cards.will.time': '~8 мин',
  'v2.cards.will.cta': 'Составить завещание',
  'v2.cards.will.strap': 'Наследие',
  'v2.cards.will.benefits': 'Лимит 1/3 · Опека · Семейное письмо',
  'v2.community.kicker': 'Поделитесь благом',
  'v2.community.title.a': 'Принесите Amanah by Zakat.International',
  'v2.community.title.b': 'в свою общину',
  'v2.community.subtitle': 'Бесплатный набор из 8 шаблонов для планирования наследства и печатный A4-постер для мечети с QR-кодом для местной доски объявлений.',
  'v2.faq.kicker': 'Центр знаний',
  'v2.faq.title.a': 'Закят, фараид и васыя —',
  'v2.faq.title.b': 'ответы',
  'v2.faq.subtitle': 'Краткие ответы со ссылками на аят Корана или хадис, которые вы можете проверить сами.',
  'v2.faq.search': 'Спросите что угодно — серебряный нисаб, лимит васыи 1/3, хранение данных…',
  'v2.faq.cat.all': 'Все',
  'v2.faq.cat.zakat': 'Закят',
  'v2.faq.cat.faraid': 'Фараид',
  'v2.faq.cat.wasiyyah': 'Васыя',
  'v2.faq.cat.privacy': 'Приватность',
  'v2.faq.count.answer': 'ответ',
  'v2.faq.count.answers': 'ответов',
  'v2.faq.empty.title': 'Мы ещё не разобрали этот вопрос.',
  'v2.faq.empty.body': 'Попробуйте короче или другую категорию — или спросите ИИ-советника на странице результатов наследства.',
  'v2.faq.footer': 'Не нашли что искали? Откройте ИИ-советника на странице результатов наследства — он объяснит ваш случай простым языком, не выходя из вашего браузера.',
};

const hi = {
  'v2.hero.kicker': '95 देशों के मुसलमानों का विश्वास',
  'v2.hero.title.a': 'मुसलमानों की रक्षा में मदद',
  'v2.hero.title.b': 'धन, परिवार',
  'v2.hero.title.c': 'और',
  'v2.hero.title.d': 'विरासत',
  'v2.hero.subtitle': 'ज़कात, फ़राइद और वसीयत के लिए एक निजी टूलकिट।',
  'v2.hero.subtitle.tail': 'कोई खाता नहीं। कोई ट्रैकिंग नहीं। हमेशा मुफ़्त।',
  'v2.hero.cta.primary': 'अपनी यात्रा चुनें',
  'v2.hero.cta.kit': 'जीवन-योजना किट',
  'v2.hero.cta.kitBusy': 'तैयार हो रहा है…',
  'v2.hero.cta.country': '95 देश गाइड',
  'v2.hero.stats.muslims': 'मुस्लिम',
  'v2.hero.stats.countries': 'देश',
  'v2.hero.stats.languages': 'भाषाएँ',
  'v2.hero.stats.private': 'निजी',
  'v2.hero.stats.stored': 'संग्रहीत',
  'v2.cards.kicker': 'अपनी यात्रा चुनें',
  'v2.cards.zakat.title': 'ज़कात की गणना करें',
  'v2.cards.zakat.subtitle': 'सोना, बचत, संपत्ति, व्यवसाय और निवेश पर।',
  'v2.cards.zakat.time': '~2 मिनट',
  'v2.cards.zakat.cta': 'गणना शुरू करें',
  'v2.cards.zakat.strap': 'शुद्धिकरण',
  'v2.cards.zakat.benefits': '5 मज़हब · लाइव निसाब · मुद्रा-अनुकूल',
  'v2.cards.faraid.title': 'इस्लामी विरासत',
  'v2.cards.faraid.subtitle': 'फ़राइद हिस्से — विश्वास और स्पष्टता के साथ।',
  'v2.cards.faraid.time': '~4 मिनट',
  'v2.cards.faraid.cta': 'हिस्से गणना करें',
  'v2.cards.faraid.strap': 'वितरण',
  'v2.cards.faraid.benefits': 'सभी मज़हब · परिवार वृक्ष · अवल · रद्द · हज्ब',
  'v2.cards.will.title': 'इस्लामी वसीयत',
  'v2.cards.will.subtitle': 'शरीयत-अनुरूप वसीयत — अपनों की रक्षा करें।',
  'v2.cards.will.time': '~8 मिनट',
  'v2.cards.will.cta': 'अपनी वसीयत बनाएं',
  'v2.cards.will.strap': 'विरासत',
  'v2.cards.will.benefits': '1/3 सीमा · संरक्षकता · पारिवारिक पत्र',
  'v2.community.kicker': 'अच्छाई साझा करें',
  'v2.community.title.a': 'Amanah by Zakat.International को',
  'v2.community.title.b': 'अपने समुदाय तक ले जाएँ',
  'v2.community.subtitle': 'आपके लिए 8 टेम्पलेट वाली मुफ़्त एस्टेट-प्लानिंग किट, और आपके स्थानीय मस्जिद बोर्ड के लिए QR कोड वाला मुद्रण योग्य A4 मस्जिद पोस्टर।',
  'v2.faq.kicker': 'ज्ञान केंद्र',
  'v2.faq.title.a': 'ज़कात, फ़राइद और वसीयत —',
  'v2.faq.title.b': 'उत्तर',
  'v2.faq.subtitle': 'संक्षिप्त उत्तर, जिन्हें आप स्वयं देखकर मिलान कर सकते हैं — क़ुरआन आयत या हदीस के हवाले के साथ।',
  'v2.faq.search': 'कुछ भी पूछें — चांदी निसाब, 1/3 वसीयत, डेटा स्टोरेज…',
  'v2.faq.cat.all': 'सभी',
  'v2.faq.cat.zakat': 'ज़कात',
  'v2.faq.cat.faraid': 'फ़राइद',
  'v2.faq.cat.wasiyyah': 'वसीयत',
  'v2.faq.cat.privacy': 'गोपनीयता',
  'v2.faq.count.answer': 'उत्तर',
  'v2.faq.count.answers': 'उत्तर',
  'v2.faq.empty.title': 'हमने अभी इसे नहीं छुआ है।',
  'v2.faq.empty.body': 'छोटा वाक्यांश या दूसरी श्रेणी आज़माएँ — या विरासत परिणाम पृष्ठ पर AI सलाहकार से पूछें।',
  'v2.faq.footer': 'आप जो ढूँढ रहे हैं वह नहीं मिला? विरासत परिणाम पृष्ठ पर AI सलाहकार खोलें — यह आपके विशिष्ट मामले को सरल भाषा में समझाएगा, बिना आपके ब्राउज़र से बाहर निकले।',
};

const bn = {
  'v2.hero.kicker': '95 দেশের মুসলমানদের আস্থা',
  'v2.hero.title.a': 'মুসলমানদের রক্ষা করছি',
  'v2.hero.title.b': 'সম্পদ, পরিবার',
  'v2.hero.title.c': 'এবং',
  'v2.hero.title.d': 'উত্তরাধিকার',
  'v2.hero.subtitle': 'যাকাত, ফারায়েজ এবং ওসিয়তের জন্য একটি ব্যক্তিগত টুলকিট।',
  'v2.hero.subtitle.tail': 'কোনো অ্যাকাউন্ট নয়। কোনো ট্র্যাকিং নয়। চিরকাল বিনামূল্যে।',
  'v2.hero.cta.primary': 'আপনার যাত্রা বেছে নিন',
  'v2.hero.cta.kit': 'জীবন-পরিকল্পনা কিট',
  'v2.hero.cta.kitBusy': 'প্রস্তুত হচ্ছে…',
  'v2.hero.cta.country': '95টি দেশের গাইড',
  'v2.hero.stats.muslims': 'মুসলিম',
  'v2.hero.stats.countries': 'দেশ',
  'v2.hero.stats.languages': 'ভাষা',
  'v2.hero.stats.private': 'ব্যক্তিগত',
  'v2.hero.stats.stored': 'সংরক্ষিত',
  'v2.cards.kicker': 'আপনার যাত্রা বেছে নিন',
  'v2.cards.zakat.title': 'যাকাত গণনা করুন',
  'v2.cards.zakat.subtitle': 'স্বর্ণ, সঞ্চয়, সম্পত্তি, ব্যবসা এবং বিনিয়োগে।',
  'v2.cards.zakat.time': '~2 মিনিট',
  'v2.cards.zakat.cta': 'গণনা শুরু করুন',
  'v2.cards.zakat.strap': 'পবিত্রকরণ',
  'v2.cards.zakat.benefits': '5 মাযহাব · লাইভ নিসাব · বহু-মুদ্রা',
  'v2.cards.faraid.title': 'ইসলামী উত্তরাধিকার',
  'v2.cards.faraid.subtitle': 'ফারায়েজ অংশ — আত্মবিশ্বাস ও স্পষ্টতা সহ।',
  'v2.cards.faraid.time': '~4 মিনিট',
  'v2.cards.faraid.cta': 'অংশ গণনা করুন',
  'v2.cards.faraid.strap': 'বণ্টন',
  'v2.cards.faraid.benefits': 'সব মাযহাব · পারিবারিক বৃক্ষ · আউল · রদ্দ · হাজব',
  'v2.cards.will.title': 'ইসলামী ওসিয়ত',
  'v2.cards.will.subtitle': 'শরীয়ত-সঙ্গত ওসিয়ত — প্রিয়জনকে রক্ষা করুন।',
  'v2.cards.will.time': '~8 মিনিট',
  'v2.cards.will.cta': 'আপনার ওসিয়ত লিখুন',
  'v2.cards.will.strap': 'উত্তরাধিকার',
  'v2.cards.will.benefits': '1/3 সীমা · অভিভাবকত্ব · পারিবারিক চিঠি',
  'v2.community.kicker': 'ভালো কাজ ভাগ করুন',
  'v2.community.title.a': 'Amanah by Zakat.International-কে',
  'v2.community.title.b': 'আপনার সম্প্রদায়ে নিয়ে যান',
  'v2.community.subtitle': 'আপনার জন্য 8-টেমপ্লেট বিনামূল্যে এস্টেট-পরিকল্পনা কিট, এবং আপনার স্থানীয় মসজিদ বোর্ডের জন্য QR কোড সহ মুদ্রণযোগ্য A4 মসজিদ পোস্টার।',
  'v2.faq.kicker': 'জ্ঞান কেন্দ্র',
  'v2.faq.title.a': 'যাকাত, ফারায়েজ ও ওসিয়ত —',
  'v2.faq.title.b': 'উত্তর',
  'v2.faq.subtitle': 'কুরআনের আয়াত বা হাদিসের রেফারেন্স সহ সংক্ষিপ্ত উত্তর যা আপনি নিজেই যাচাই করতে পারেন।',
  'v2.faq.search': 'যা কিছু জিজ্ঞাসা করুন — রৌপ্য নিসাব, 1/3 ওসিয়ত, ডেটা সংরক্ষণ…',
  'v2.faq.cat.all': 'সব',
  'v2.faq.cat.zakat': 'যাকাত',
  'v2.faq.cat.faraid': 'ফারায়েজ',
  'v2.faq.cat.wasiyyah': 'ওসিয়ত',
  'v2.faq.cat.privacy': 'গোপনীয়তা',
  'v2.faq.count.answer': 'উত্তর',
  'v2.faq.count.answers': 'উত্তর',
  'v2.faq.empty.title': 'আমরা এখনো এটি স্পর্শ করিনি।',
  'v2.faq.empty.body': 'ছোট বাক্যাংশ বা ভিন্ন শ্রেণী চেষ্টা করুন — বা উত্তরাধিকার ফলাফল পাতায় AI উপদেষ্টাকে জিজ্ঞাসা করুন।',
  'v2.faq.footer': 'যা খুঁজছেন পাচ্ছেন না? উত্তরাধিকার ফলাফল পাতায় AI উপদেষ্টা খুলুন — এটি আপনার নির্দিষ্ট কেসটি সহজ ভাষায় ব্যাখ্যা করবে, আপনার ব্রাউজার ছাড়াই।',
};

const ml = {
  'v2.hero.kicker': '95 രാജ്യങ്ങളിലെ മുസ്ലിംകൾ വിശ്വസിക്കുന്നു',
  'v2.hero.title.a': 'മുസ്ലിംകളെ സംരക്ഷിക്കാൻ സഹായിക്കുന്നു',
  'v2.hero.title.b': 'സമ്പത്ത്, കുടുംബം',
  'v2.hero.title.c': 'ഒപ്പം',
  'v2.hero.title.d': 'പാരമ്പര്യം',
  'v2.hero.subtitle': 'സകാത്, ഫറാഇദ്, വസിയ്യത്ത് എന്നിവയ്ക്ക് ഒരു സ്വകാര്യ ടൂൾകിറ്റ്.',
  'v2.hero.subtitle.tail': 'അക്കൗണ്ട് വേണ്ട. ട്രാക്കിംഗ് ഇല്ല. എന്നും സൗജന്യം.',
  'v2.hero.cta.primary': 'നിങ്ങളുടെ യാത്ര തിരഞ്ഞെടുക്കുക',
  'v2.hero.cta.kit': 'ജീവിത-ആസൂത്രണ കിറ്റ്',
  'v2.hero.cta.kitBusy': 'തയ്യാറാക്കുന്നു…',
  'v2.hero.cta.country': '95 രാജ്യ ഗൈഡുകൾ',
  'v2.hero.stats.muslims': 'മുസ്ലിംകൾ',
  'v2.hero.stats.countries': 'രാജ്യങ്ങൾ',
  'v2.hero.stats.languages': 'ഭാഷകൾ',
  'v2.hero.stats.private': 'സ്വകാര്യം',
  'v2.hero.stats.stored': 'സൂക്ഷിക്കപ്പെട്ടത്',
  'v2.cards.kicker': 'നിങ്ങളുടെ യാത്ര തിരഞ്ഞെടുക്കുക',
  'v2.cards.zakat.title': 'സകാത് കണക്കാക്കുക',
  'v2.cards.zakat.subtitle': 'സ്വർണ്ണം, സമ്പാദ്യം, സ്വത്ത്, ബിസിനസ്, നിക്ഷേപം.',
  'v2.cards.zakat.time': '~2 മിനിറ്റ്',
  'v2.cards.zakat.cta': 'കണക്കാക്കൽ ആരംഭിക്കുക',
  'v2.cards.zakat.strap': 'ശുദ്ധീകരണം',
  'v2.cards.zakat.benefits': '5 മദ്ഹബ് · ലൈവ് നിസാബ് · ബഹു-കറൻസി',
  'v2.cards.faraid.title': 'ഇസ്ലാമിക അനന്തരാവകാശം',
  'v2.cards.faraid.subtitle': 'ഫറാഇദ് ഓഹരികൾ — ആത്മവിശ്വാസത്തോടെ.',
  'v2.cards.faraid.time': '~4 മിനിറ്റ്',
  'v2.cards.faraid.cta': 'ഓഹരികൾ കണക്കാക്കുക',
  'v2.cards.faraid.strap': 'വിതരണം',
  'v2.cards.faraid.benefits': 'എല്ലാ മദ്ഹബും · കുടുംബ വൃക്ഷം · ഔൽ · റദ്ദ് · ഹജ്ബ്',
  'v2.cards.will.title': 'ഇസ്ലാമിക വസിയ്യത്ത്',
  'v2.cards.will.subtitle': 'ശരീഅത്ത്-സമ്മത വസിയ്യത്ത് — പ്രിയപ്പെട്ടവരെ സംരക്ഷിക്കുക.',
  'v2.cards.will.time': '~8 മിനിറ്റ്',
  'v2.cards.will.cta': 'നിങ്ങളുടെ വസിയ്യത്ത് എഴുതുക',
  'v2.cards.will.strap': 'പാരമ്പര്യം',
  'v2.cards.will.benefits': '1/3 പരിധി · സംരക്ഷണം · കുടുംബ കത്ത്',
  'v2.community.kicker': 'നന്മ പങ്കിടുക',
  'v2.community.title.a': 'Amanah by Zakat.International',
  'v2.community.title.b': 'നിങ്ങളുടെ സമൂഹത്തിലേക്ക് കൊണ്ടുപോകുക',
  'v2.community.subtitle': 'നിങ്ങൾക്ക് സൗജന്യമായ 8-ടെംപ്ലേറ്റ് എസ്റ്റേറ്റ്-പ്ലാനിംഗ് കിറ്റും, നിങ്ങളുടെ പ്രാദേശിക പള്ളി ബോർഡിനായി QR കോഡ് ഉള്ള പ്രിന്റബിൾ A4 പള്ളി പോസ്റ്ററും.',
  'v2.faq.kicker': 'അറിവ് കേന്ദ്രം',
  'v2.faq.title.a': 'സകാത്, ഫറാഇദ്, വസിയ്യത്ത് —',
  'v2.faq.title.b': 'ഉത്തരങ്ങൾ',
  'v2.faq.subtitle': 'നിങ്ങൾക്ക് സ്വയം പരിശോധിക്കാവുന്ന ഖുർആൻ ആയത്തിനെയോ ഹദീസിനെയോ അടിസ്ഥാനമാക്കിയ ഹ്രസ്വ ഉത്തരങ്ങൾ.',
  'v2.faq.search': 'എന്തും ചോദിക്കുക — വെള്ളി നിസാബ്, 1/3 വസിയ്യത്ത്, ഡാറ്റാ സംഭരണം…',
  'v2.faq.cat.all': 'എല്ലാം',
  'v2.faq.cat.zakat': 'സകാത്',
  'v2.faq.cat.faraid': 'ഫറാഇദ്',
  'v2.faq.cat.wasiyyah': 'വസിയ്യത്ത്',
  'v2.faq.cat.privacy': 'സ്വകാര്യത',
  'v2.faq.count.answer': 'ഉത്തരം',
  'v2.faq.count.answers': 'ഉത്തരങ്ങൾ',
  'v2.faq.empty.title': 'അതു ഞങ്ങൾ ഇതുവരെ കൈകാര്യം ചെയ്തിട്ടില്ല.',
  'v2.faq.empty.body': 'ചെറിയ വാചകം അല്ലെങ്കിൽ മറ്റൊരു വിഭാഗം പരീക്ഷിക്കുക — അല്ലെങ്കിൽ അനന്തരാവകാശ ഫല പേജിലെ AI ഉപദേഷ്ടാവിനോട് ചോദിക്കുക.',
  'v2.faq.footer': 'തിരയുന്നത് കണ്ടെത്തിയില്ലേ? അനന്തരാവകാശ ഫല പേജിലെ AI ഉപദേഷ്ടാവ് തുറക്കുക — ഇത് നിങ്ങളുടെ പ്രത്യേക കേസ് ലളിതമായ ഭാഷയിൽ വിശദീകരിക്കും, നിങ്ങളുടെ ബ്രൗസർ വിട്ട് പോകാതെ.',
};

const id = {
  'v2.hero.kicker': 'Dipercaya Muslim di 95 negara',
  'v2.hero.title.a': 'Membantu Muslim melindungi',
  'v2.hero.title.b': 'harta, keluarga',
  'v2.hero.title.c': 'dan',
  'v2.hero.title.d': 'warisan',
  'v2.hero.subtitle': 'Satu peralatan pribadi untuk Zakat, Faraid, dan Wasiyyah.',
  'v2.hero.subtitle.tail': 'Tanpa akun. Tanpa pelacakan. Gratis selamanya.',
  'v2.hero.cta.primary': 'Pilih Perjalanan Anda',
  'v2.hero.cta.kit': 'Kit perencanaan hidup',
  'v2.hero.cta.kitBusy': 'Menyiapkan…',
  'v2.hero.cta.country': 'Panduan 95 negara',
  'v2.hero.stats.muslims': 'Muslim',
  'v2.hero.stats.countries': 'Negara',
  'v2.hero.stats.languages': 'Bahasa',
  'v2.hero.stats.private': 'Pribadi',
  'v2.hero.stats.stored': 'Disimpan',
  'v2.cards.kicker': 'Pilih perjalanan Anda',
  'v2.cards.zakat.title': 'Hitung Zakat',
  'v2.cards.zakat.subtitle': 'Emas, tabungan, properti, bisnis, & investasi.',
  'v2.cards.zakat.time': '~2 mnt',
  'v2.cards.zakat.cta': 'Mulai hitung',
  'v2.cards.zakat.strap': 'Penyucian',
  'v2.cards.zakat.benefits': '5 mazhab · Nisab langsung · Multi mata uang',
  'v2.cards.faraid.title': 'Waris Islam (Faraid)',
  'v2.cards.faraid.subtitle': 'Bagian Faraid — dengan jelas dan yakin.',
  'v2.cards.faraid.time': '~4 mnt',
  'v2.cards.faraid.cta': 'Hitung bagian',
  'v2.cards.faraid.strap': 'Pembagian',
  'v2.cards.faraid.benefits': 'Semua mazhab · Silsilah · Awl · Radd · Hajb',
  'v2.cards.will.title': 'Wasiat Islam (Wasiyyah)',
  'v2.cards.will.subtitle': 'Wasiat sesuai syariah — lindungi orang tercinta.',
  'v2.cards.will.time': '~8 mnt',
  'v2.cards.will.cta': 'Buat wasiat',
  'v2.cards.will.strap': 'Warisan',
  'v2.cards.will.benefits': 'Batas 1/3 · Perwalian · Surat keluarga',
  'v2.community.kicker': 'Sebarkan kebaikan',
  'v2.community.title.a': 'Bawa Amanah by Zakat.International',
  'v2.community.title.b': 'ke komunitas Anda',
  'v2.community.subtitle': 'Kit perencanaan waris gratis dengan 8 templat, dan poster masjid A4 dapat dicetak dengan kode QR untuk papan masjid setempat.',
  'v2.faq.kicker': 'Pusat Pengetahuan',
  'v2.faq.title.a': 'Zakat, Faraid & Wasiyyah —',
  'v2.faq.title.b': 'terjawab',
  'v2.faq.subtitle': 'Jawaban ringkas dirujuk ke ayat Al-Qur\u2019an atau hadis yang bisa Anda cek sendiri.',
  'v2.faq.search': 'Tanya apa saja — nisab perak, wasiyyah 1/3, penyimpanan data…',
  'v2.faq.cat.all': 'Semua',
  'v2.faq.cat.zakat': 'Zakat',
  'v2.faq.cat.faraid': 'Faraid',
  'v2.faq.cat.wasiyyah': 'Wasiyyah',
  'v2.faq.cat.privacy': 'Privasi',
  'v2.faq.count.answer': 'jawaban',
  'v2.faq.count.answers': 'jawaban',
  'v2.faq.empty.title': 'Kami belum membahasnya.',
  'v2.faq.empty.body': 'Coba frasa lebih pendek atau kategori berbeda — atau tanya Penasihat AI di halaman hasil Faraid.',
  'v2.faq.footer': 'Tidak menemukan yang dicari? Buka Penasihat AI di halaman hasil Faraid — menjelaskan kasus Anda dalam bahasa sederhana, tanpa meninggalkan peramban Anda.',
};

const fa = {
  'v2.hero.kicker': 'مورد اعتماد مسلمانان در ۹۵ کشور',
  'v2.hero.title.a': 'کمک به مسلمانان برای محافظت از',
  'v2.hero.title.b': 'ثروت، خانواده',
  'v2.hero.title.c': 'و',
  'v2.hero.title.d': 'میراث',
  'v2.hero.subtitle': 'یک جعبه ابزار خصوصی برای زکات، فرائض و وصیت.',
  'v2.hero.subtitle.tail': 'بدون حساب. بدون رهگیری. همیشه رایگان.',
  'v2.hero.cta.primary': 'مسیر خود را انتخاب کنید',
  'v2.hero.cta.kit': 'کیت برنامه‌ریزی زندگی',
  'v2.hero.cta.kitBusy': 'در حال آماده‌سازی…',
  'v2.hero.cta.country': 'راهنمای ۹۵ کشور',
  'v2.hero.stats.muslims': 'مسلمان',
  'v2.hero.stats.countries': 'کشور',
  'v2.hero.stats.languages': 'زبان',
  'v2.hero.stats.private': 'خصوصی',
  'v2.hero.stats.stored': 'ذخیره‌شده',
  'v2.cards.kicker': 'مسیر خود را انتخاب کنید',
  'v2.cards.zakat.title': 'محاسبه زکات',
  'v2.cards.zakat.subtitle': 'طلا، پس‌انداز، ملک، کسب‌وکار و سرمایه‌گذاری.',
  'v2.cards.zakat.time': '~۲ دقیقه',
  'v2.cards.zakat.cta': 'شروع محاسبه',
  'v2.cards.zakat.strap': 'تطهیر',
  'v2.cards.zakat.benefits': '۵ مذهب · نصاب زنده · چند ارزی',
  'v2.cards.faraid.title': 'ارث اسلامی',
  'v2.cards.faraid.subtitle': 'سهام فرائض — با اطمینان و وضوح.',
  'v2.cards.faraid.time': '~۴ دقیقه',
  'v2.cards.faraid.cta': 'محاسبه سهم‌ها',
  'v2.cards.faraid.strap': 'توزیع',
  'v2.cards.faraid.benefits': 'همه مذاهب · شجره · عول · ردّ · حجب',
  'v2.cards.will.title': 'وصیت اسلامی',
  'v2.cards.will.subtitle': 'وصیت مطابق شریعت — از عزیزان محافظت کنید.',
  'v2.cards.will.time': '~۸ دقیقه',
  'v2.cards.will.cta': 'نوشتن وصیت',
  'v2.cards.will.strap': 'میراث',
  'v2.cards.will.benefits': 'سقف ۱/۳ · قیمومیت · نامه خانواده',
  'v2.community.kicker': 'خیر را به اشتراک بگذارید',
  'v2.community.title.a': 'زکات اینترنشنال را',
  'v2.community.title.b': 'به جامعه خود ببرید',
  'v2.community.subtitle': 'یک کیت رایگان برنامه‌ریزی اموال با ۸ الگو برای شما، و پوستر مسجد A4 قابل چاپ با کد QR برای تابلوی مسجد محلی.',
  'v2.faq.kicker': 'مرکز دانش',
  'v2.faq.title.a': 'زکات، فرائض و وصیت —',
  'v2.faq.title.b': 'پاسخ',
  'v2.faq.subtitle': 'پاسخ‌های کوتاه با ارجاع به آیه قرآن یا حدیثی که خود می‌توانید ببینید.',
  'v2.faq.search': 'هر چیزی بپرسید — نصاب نقره، ۱/۳ وصیت، ذخیره داده…',
  'v2.faq.cat.all': 'همه',
  'v2.faq.cat.zakat': 'زکات',
  'v2.faq.cat.faraid': 'فرائض',
  'v2.faq.cat.wasiyyah': 'وصیت',
  'v2.faq.cat.privacy': 'حریم خصوصی',
  'v2.faq.count.answer': 'پاسخ',
  'v2.faq.count.answers': 'پاسخ',
  'v2.faq.empty.title': 'هنوز به این موضوع نپرداخته‌ایم.',
  'v2.faq.empty.body': 'عبارتی کوتاه‌تر یا دسته‌ای دیگر امتحان کنید — یا از مشاور هوش مصنوعی در صفحه نتیجه ارث بپرسید.',
  'v2.faq.footer': 'آنچه می‌خواهید را نیافتید؟ مشاور هوش مصنوعی در صفحه نتیجه ارث را باز کنید — پرونده شما را به زبان ساده توضیح می‌دهد، بدون خروج از مرورگر.',
};

// Dari — reuse Farsi as Farsi/Dari share most V2 vocabulary
const prs = { ...fa };

const sw = {
  'v2.hero.kicker': 'Waaminifu na Waislamu katika nchi 95',
  'v2.hero.title.a': 'Kusaidia Waislamu kulinda',
  'v2.hero.title.b': 'utajiri, familia',
  'v2.hero.title.c': 'na',
  'v2.hero.title.d': 'urithi',
  'v2.hero.subtitle': 'Zana moja ya faragha kwa Zakat, Faraid na Wasiyyah.',
  'v2.hero.subtitle.tail': 'Hakuna akaunti. Hakuna ufuatiliaji. Bure milele.',
  'v2.hero.cta.primary': 'Chagua Safari Yako',
  'v2.hero.cta.kit': 'Kifurushi cha kupanga maisha',
  'v2.hero.cta.kitBusy': 'Inaandaa…',
  'v2.hero.cta.country': 'Miongozo ya nchi 95',
  'v2.hero.stats.muslims': 'Waislamu',
  'v2.hero.stats.countries': 'Nchi',
  'v2.hero.stats.languages': 'Lugha',
  'v2.hero.stats.private': 'Faragha',
  'v2.hero.stats.stored': 'Iliyohifadhiwa',
  'v2.cards.kicker': 'Chagua safari yako',
  'v2.cards.zakat.title': 'Kokotoa Zakat',
  'v2.cards.zakat.subtitle': 'Dhahabu, akiba, mali, biashara na uwekezaji.',
  'v2.cards.zakat.time': '~dak 2',
  'v2.cards.zakat.cta': 'Anza kukokotoa',
  'v2.cards.zakat.strap': 'Utakaso',
  'v2.cards.zakat.benefits': 'Madhehebu 5 · Nisab hai · Sarafu nyingi',
  'v2.cards.faraid.title': 'Urithi wa Kiislamu',
  'v2.cards.faraid.subtitle': 'Sehemu za Faraid — kwa uhakika na uwazi.',
  'v2.cards.faraid.time': '~dak 4',
  'v2.cards.faraid.cta': 'Kokotoa sehemu',
  'v2.cards.faraid.strap': 'Ugawaji',
  'v2.cards.faraid.benefits': 'Madhehebu yote · Ukoo · Awl · Radd · Hajb',
  'v2.cards.will.title': 'Wasiyyah ya Kiislamu',
  'v2.cards.will.subtitle': 'Wasiyyah kulingana na Sharia — linda wapendwa wako.',
  'v2.cards.will.time': '~dak 8',
  'v2.cards.will.cta': 'Andika wasiyyah',
  'v2.cards.will.strap': 'Urithi',
  'v2.cards.will.benefits': 'Kikomo cha 1/3 · Ulinzi · Barua ya familia',
  'v2.community.kicker': 'Shirikisha jema',
  'v2.community.title.a': 'Peleka Amanah by Zakat.International',
  'v2.community.title.b': 'katika jamii yako',
  'v2.community.subtitle': 'Kifurushi cha bure cha upangaji urithi cha violezo 8, na bango la msikiti la A4 linaloweza kuchapishwa lenye msimbo wa QR kwa ubao wa msikiti wa eneo lako.',
  'v2.faq.kicker': 'Kituo cha Maarifa',
  'v2.faq.title.a': 'Zakat, Faraid na Wasiyyah —',
  'v2.faq.title.b': 'majibu',
  'v2.faq.subtitle': 'Majibu mafupi yanayorejelewa kwa aya ya Qur\u2019ani au hadithi ambazo unaweza kuzichunguza mwenyewe.',
  'v2.faq.search': 'Uliza chochote — nisab ya fedha, wasiyyah 1/3, uhifadhi wa data…',
  'v2.faq.cat.all': 'Yote',
  'v2.faq.cat.zakat': 'Zakat',
  'v2.faq.cat.faraid': 'Faraid',
  'v2.faq.cat.wasiyyah': 'Wasiyyah',
  'v2.faq.cat.privacy': 'Faragha',
  'v2.faq.count.answer': 'jibu',
  'v2.faq.count.answers': 'majibu',
  'v2.faq.empty.title': 'Bado hatujashughulikia hilo.',
  'v2.faq.empty.body': 'Jaribu maneno mafupi zaidi au kategoria tofauti — au muulize Mshauri wa AI kwenye ukurasa wa matokeo ya urithi.',
  'v2.faq.footer': 'Hujakipata unachotafuta? Fungua Mshauri wa AI kwenye ukurasa wa matokeo ya urithi — hueleza kesi yako kwa lugha rahisi, bila kuondoka kwenye kivinjari chako.',
};

const uz = {
  'v2.hero.kicker': '95 mamlakat musulmonlari ishonchida',
  'v2.hero.title.a': 'Musulmonlarni himoya qilishga yordam',
  'v2.hero.title.b': 'mol-mulk, oila',
  'v2.hero.title.c': 'va',
  'v2.hero.title.d': 'meros',
  'v2.hero.subtitle': 'Zakot, faroiz va vasiyat uchun bitta shaxsiy asboblar to\u2018plami.',
  'v2.hero.subtitle.tail': 'Akkaunt kerak emas. Kuzatuv yo\u2018q. Doim bepul.',
  'v2.hero.cta.primary': 'Yo\u2018lingizni tanlang',
  'v2.hero.cta.kit': 'Hayotni rejalashtirish to\u2018plami',
  'v2.hero.cta.kitBusy': 'Tayyorlanmoqda…',
  'v2.hero.cta.country': '95 mamlakat qo\u2018llanmasi',
  'v2.hero.stats.muslims': 'Musulmon',
  'v2.hero.stats.countries': 'Mamlakat',
  'v2.hero.stats.languages': 'Til',
  'v2.hero.stats.private': 'Shaxsiy',
  'v2.hero.stats.stored': 'Saqlangan',
  'v2.cards.kicker': 'Yo\u2018lingizni tanlang',
  'v2.cards.zakat.title': 'Zakot hisoblash',
  'v2.cards.zakat.subtitle': 'Oltin, jamg\u2018arma, mulk, biznes va investitsiyalarga.',
  'v2.cards.zakat.time': '~2 daq',
  'v2.cards.zakat.cta': 'Hisoblashni boshlang',
  'v2.cards.zakat.strap': 'Poklanish',
  'v2.cards.zakat.benefits': '5 mazhab · Jonli nisob · Ko\u2018p valyuta',
  'v2.cards.faraid.title': 'Islom merosi (faroiz)',
  'v2.cards.faraid.subtitle': 'Faroiz ulushlari — ishonch va oydinlik bilan.',
  'v2.cards.faraid.time': '~4 daq',
  'v2.cards.faraid.cta': 'Ulushlarni hisoblang',
  'v2.cards.faraid.strap': 'Taqsimlash',
  'v2.cards.faraid.benefits': 'Barcha mazhablar · Oila daraxti · Avl · Radd · Hajb',
  'v2.cards.will.title': 'Islomiy vasiyat',
  'v2.cards.will.subtitle': 'Shariatga muvofiq vasiyat — yaqinlaringizni himoya qiling.',
  'v2.cards.will.time': '~8 daq',
  'v2.cards.will.cta': 'Vasiyat yozing',
  'v2.cards.will.strap': 'Meros',
  'v2.cards.will.benefits': '1/3 chegara · Homiylik · Oilaviy xat',
  'v2.community.kicker': 'Yaxshilik ulashing',
  'v2.community.title.a': 'Amanah by Zakat.International\u2019ni',
  'v2.community.title.b': 'jamoangizga olib boring',
  'v2.community.subtitle': 'Sizga 8 shabloli bepul meros rejalashtirish to\u2018plami va mahalliy masjid taxtangiz uchun QR-kodli chop etiladigan A4 masjid plakati.',
  'v2.faq.kicker': 'Bilim markazi',
  'v2.faq.title.a': 'Zakot, faroiz va vasiyat —',
  'v2.faq.title.b': 'javoblar',
  'v2.faq.subtitle': 'O\u2018zingiz tekshira oladigan Qur\u2019on oyati yoki hadisiga havola qilingan qisqa javoblar.',
  'v2.faq.search': 'Xohlagan narsani so\u2018rang — kumush nisob, 1/3 vasiyat, ma\u2019lumot saqlash…',
  'v2.faq.cat.all': 'Barchasi',
  'v2.faq.cat.zakat': 'Zakot',
  'v2.faq.cat.faraid': 'Faroiz',
  'v2.faq.cat.wasiyyah': 'Vasiyat',
  'v2.faq.cat.privacy': 'Maxfiylik',
  'v2.faq.count.answer': 'javob',
  'v2.faq.count.answers': 'javoblar',
  'v2.faq.empty.title': 'Buni hali qamrab olmaganmiz.',
  'v2.faq.empty.body': 'Qisqaroq ibora yoki boshqa kategoriyani sinab ko\u2018ring — yoki meros natija sahifasidagi AI Maslahatchisidan so\u2018rang.',
  'v2.faq.footer': 'Izlaganingizni topmadingizmi? Meros natija sahifasidagi AI Maslahatchisini oching — u kelasingizni sodda tilda tushuntiradi, brauzeringizdan chiqmasdan.',
};

export const translationsV2 = { en, ar, ur, fr, ms, tr, ru, hi, bn, ml, id, fa, prs, sw, uz };

export default translationsV2;
